import type { ComponentProps, VFC } from 'react';
import React, { useMemo } from 'react';

import { PagePropertiesReportRenderer } from '@confluence/page-properties-report-renderer';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { MustPreloadLoadableNames, mustPreloadWhenRenderingSPA } from '@confluence/loadable';

import { getMacroId } from './utils.ts';

type DetailsSummaryExtensionHandlerProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: ComponentProps<typeof PagePropertiesReportRenderer>['macroParams'];
	openAnchorsInNewTab?: boolean;
};

export const DetailsSummaryExtensionHandler: VFC<DetailsSummaryExtensionHandlerProps> = ({
	macroDefaultProps,
	macroParams,
	openAnchorsInNewTab,
}) => {
	mustPreloadWhenRenderingSPA([
		MustPreloadLoadableNames.ClassicRendererLoader,
		MustPreloadLoadableNames.ClassicEditorContextProviderLoader,
	]);

	const renderServerPlaceholder = useRenderServerPlaceholder();
	const macroId = getMacroId(macroDefaultProps.node);

	const macroDefaultPropsWithMacroId = useMemo(
		() => ({
			...macroDefaultProps,
			attributes: {
				...macroDefaultProps.attributes,
				macroId: macroDefaultProps.attributes?.macroId || macroId,
			},
		}),
		[macroDefaultProps, macroId],
	);

	return (
		<div
			data-fabric-macro={macroId}
			data-vc="page-properties-report-macro"
			{...(renderServerPlaceholder
				? { 'data-ssr-placeholder': macroId }
				: { 'data-ssr-placeholder-replace': macroId })}
		>
			<PagePropertiesReportRenderer
				macroDefaultProps={macroDefaultPropsWithMacroId}
				macroParams={macroParams}
				openAnchorsInNewTab={openAnchorsInNewTab}
			/>
		</div>
	);
};
